/**
 * ContextCard: section 4 of the Screener page -- one card, deduped, every figure carrying its own as-of. Moved
 * verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Nothing here ranks or sizes.
 */
import React from "react";
import { MICRO_LABEL, SectionCard } from "@components/cockpit";
import { ContextTile, mutedText, secondaryText, ui } from "@components/screener/screenerUi";
import { annotateOpportunityDetail, contextAsOfText, currency, effectiveTargetCaption, pctFmt, portfolioVegaText, type ContextLoadStatus } from "@lib/screenerTruth";
import { type DecisionContext, type ScreenerResponse, type ScreenerRiskContext, type SurfaceSummary, type TilesMeta } from "@lib/screenerTypes";
import { type HarvestLockout } from "@lib/harvestLockout";

export type ContextCardProps = {
  contextErrors: { snapshot?: string | undefined; kpis?: string | undefined; surface?: string | undefined; vega?: string | undefined; };
  data: ScreenerResponse | null;
  decisionContext: DecisionContext | null;
  decisionNarrative: string | null;
  defenseDisplay: { text: string; known: boolean; note: string | null; };
  harvestFetchedAtMs: number | null;
  harvestLockout: HarvestLockout;
  navAsOfLine: string;
  portfolioVega: { total: number | null; lines: number; missingVega: number; asOf: string | null; } | null;
  riskContext: ScreenerRiskContext | null;
  riskContextStatus: ContextLoadStatus;
  runAsOfLine: string;
  surfaceSummary: SurfaceSummary | undefined;
  tilesMeta: TilesMeta;
  unknownIfBlank: (v: string | null | undefined) => string;
  vixAsOfLine: string;
};

export default function ContextCard({
  contextErrors,
  data,
  decisionContext,
  decisionNarrative,
  defenseDisplay,
  harvestFetchedAtMs,
  harvestLockout,
  navAsOfLine,
  portfolioVega,
  riskContext,
  riskContextStatus,
  runAsOfLine,
  surfaceSummary,
  tilesMeta,
  unknownIfBlank,
  vixAsOfLine,
}: ContextCardProps) {
  return (
    <section data-screener-section="context" data-testid="screener-context-section" className="mb-4">
      <SectionCard
        title="Context"
        subtitle="Risk context as read when this page loaded; run figures from the run itself. Nothing here ranks or sizes. Each figure carries its own as-of; Unknown means it could not be read."
        right={
          <div className="text-[11px] lg:text-right" style={mutedText} data-testid="screener-context-asof">
            {riskContextStatus === "loading"
              ? "Loading risk context…"
              : contextAsOfText(riskContext?.navAsOf, riskContext?.vixAsOf)}
          </div>
        }
      >
        {/* Phase 1 (finding 4): every context fetch used to fail silently and leave a blank. Say it. */}
        {contextErrors.kpis || contextErrors.surface ? (
          <div className="mb-2 text-xs" style={{ color: "rgb(var(--c-warn))" }} data-testid="screener-risk-context-unavailable">
            Context unavailable: {[contextErrors.kpis, contextErrors.surface].filter(Boolean).join(" · ")}
          </div>
        ) : null}
        {contextErrors.snapshot || contextErrors.vega ? (
          <div className="mb-2 text-xs" style={{ color: "rgb(var(--c-warn))" }} data-testid="screener-context-unavailable">
            Context unavailable: {[contextErrors.snapshot, contextErrors.vega].filter(Boolean).join(" · ")}
          </div>
        ) : null}

        {data ? (
          <div className="mb-3 rounded-lg border px-3 py-2" style={{ borderColor: ui.cardBorder }} data-testid="screener-decision-read">
            <div className={MICRO_LABEL} style={mutedText}>Run read · as of {runAsOfLine}</div>
            {/* The CIO action label and the decision prose, ONCE (the old card printed the label twice and the
                Data / Context status chips beside it; those statuses now live in the run bar and Diagnostics). */}
            <div className="mt-1 font-semibold">{decisionContext?.cioActionLabel || "Decision context not returned"}</div>
            <div className="mt-1 text-sm leading-6" style={secondaryText}>
              {decisionNarrative}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6">
          <ContextTile
            label="Base regime"
            value={unknownIfBlank(riskContext?.baseRegimeBand)}
            sub={`VIX z ${tilesMeta?.vixZScore != null ? tilesMeta.vixZScore.toFixed(2) : "Unknown"}${tilesMeta?.regime ? ` (${tilesMeta.regime})` : ""}`}
            asOf={`VIX as of ${vixAsOfLine}`}
          />
          <ContextTile
            label="Defense overlay"
            value={defenseDisplay.text}
            testId="screener-defense-overlay"
            sub={defenseDisplay.note}
            asOf={`VIX as of ${vixAsOfLine}`}
          />
          {/* CORRECTED 2026-08-13 / 2026-08-18 (CIO policy; FI target-vs-held audit item 5): funded COST basis
              against summary.funded_cap_pct -- the only number "cap" may render. Market value is uncapped and has
              its own tile. The effective regime target is not the cap and keeps its own tile. Display-only. */}
          <ContextTile
            label="Funded basis vs funded cap"
            value={`${unknownIfBlank(pctFmt(riskContext?.basisLeapsAllocationPct, 1))} / ${unknownIfBlank(pctFmt(riskContext?.fundedCapPct, 1))}`}
            sub="Cost basis vs caps.funded_max — governs new purchases"
            asOf={`NAV as of ${navAsOfLine}`}
          />
          <ContextTile
            label="Effective regime target"
            value={unknownIfBlank(pctFmt(riskContext?.basisTargetLeapsPct, 1))}
            sub={effectiveTargetCaption(riskContextStatus, riskContext?.regimeContextSource)}
            asOf={`VIX as of ${vixAsOfLine}`}
          />
          <ContextTile
            label="Market value exposure"
            value={unknownIfBlank(pctFmt(riskContext?.currentLeapsAllocationPct, 1))}
            sub="Uncapped — not a trim trigger"
            asOf={`NAV as of ${navAsOfLine}`}
          />
          <ContextTile
            label="NAV"
            value={riskContext?.portfolioNav != null ? currency(riskContext.portfolioNav) : "Unknown"}
            asOf={`NAV as of ${navAsOfLine}`}
          />
          <ContextTile
            label="Portfolio vega / pt"
            value={portfolioVegaText(portfolioVega)}
            testId="screener-portfolio-vega"
            sub="Held LEAPS: vega × qty × multiplier"
            asOf={portfolioVega?.asOf ? `Holdings as of ${portfolioVega.asOf}` : "Holdings as-of not reported"}
          />
          {/* HARVEST LOCKOUT — capital the sizing tally cannot see. Wording from the shared derivation, so this
              tile, the Risk Book Stance block and the Signals deployment track cannot disagree. Advisory only. */}
          <ContextTile
            label="Harvest lockout"
            value={harvestLockout.known ? currency(harvestLockout.rawLockedNotional) : "Unknown"}
            sub={
              harvestLockout.known
                ? harvestLockout.status === "none"
                  ? "No capital under a redeployment lockout"
                  : harvestLockout.status === "locked_not_applied"
                  ? "Under lockout — but policy does not apply it to the buy budget"
                  : "Under lockout and withheld from the LEAPS buy budget — not available to size"
                : "Lockout data unavailable — sizing cannot account for locked capital"
            }
            asOf={harvestFetchedAtMs ? `Read ${new Date(harvestFetchedAtMs).toLocaleString()}` : "Not read"}
          />
          <ContextTile
            label="Surface alignment"
            value={unknownIfBlank(pctFmt(riskContext?.surfaceAlignmentPct, 0))}
            sub="Surface page read sent to the engine's mode resolver"
            asOf="Surface overview · as-of not reported"
          />
          {data ? (
            <>
              <ContextTile
                label="Surface regime"
                value={unknownIfBlank(surfaceSummary?.surface_regime)}
                sub={`Dominant value ${surfaceSummary?.candidate_value_label || "Unknown"} (30m+ on the 3y-node proxy)`}
                asOf={surfaceSummary?.market_surface_trade_date ? `ORATS close ${surfaceSummary.market_surface_trade_date}` : `Run as of ${runAsOfLine}`}
              />
              <ContextTile
                label="Market opportunity"
                value={unknownIfBlank(decisionContext?.marketOpportunity?.label)}
                sub={
                  annotateOpportunityDetail(decisionContext?.marketOpportunity?.detail, surfaceSummary?.market_term_percentiles) ||
                  "Surface read unavailable."
                }
                asOf={`Run as of ${runAsOfLine}`}
              />
              <ContextTile
                label="Portfolio permission"
                value={unknownIfBlank(decisionContext?.portfolioPermission?.label)}
                sub={decisionContext?.portfolioPermission?.detail || "Portfolio context unavailable."}
                asOf={`Run as of ${runAsOfLine}`}
              />
            </>
          ) : null}
        </div>
      </SectionCard>
    </section>
  );
}
